import React, { useState, useEffect } from 'react';
import { Gauge, ShieldAlert, CheckCircle2, XCircle, RefreshCw, FileCheck, Image as ImageIcon, Siren } from 'lucide-react';
import { useLang } from '../i18n';
import { Card, Button, Badge, Input, Spinner, Reveal } from '../components/ui';
import SettlementTracker from '../components/SettlementTracker';
import CoverageBanner from '../components/CoverageBanner';
import api, { vnd } from '../api';

export default function Ops() {
  const { t } = useLang();
  const [claims, setClaims] = useState([]);
  const [active, setActive] = useState(null);
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState('');

  const load = () => api.get('/ops/claims').then((r) => setClaims(r.data)).catch(() => {});
  const [alerts, setAlerts] = useState([]);
  const loadAlerts = () => api.get('/ops/notifications').then((r) => setAlerts(r.data)).catch(() => {});
  useEffect(() => { load(); loadAlerts(); const iv = setInterval(loadAlerts, 20000); return () => clearInterval(iv); }, []);

  const act = async (action) => {
    setLoading(true); setDone('');
    try {
      await api.post(`/ops/claims/${active.id}/action`, { action, note });
      setDone(t('action_done')); setNote('');
      await load();
      const r = await api.get(`/claims/${active.id}`); setActive(r.data);
    } finally { setLoading(false); }
  };

  const a = active?.assessment;

  return (
    <div className="max-w-6xl mx-auto px-5 py-12" data-testid="ops-page">
      <Reveal>
        <div className="flex items-center justify-between">
          <div>
            <div className="kicker">INTERNAL</div>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold mt-2">{t('ops_h')}</h1>
            <p className="text-slate-400 mt-2">{t('ops_sub')}</p>
          </div>
          <Button variant="ghost" data-testid="ops-refresh" onClick={load}><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </Reveal>

      {alerts.filter((a) => !a.read).length > 0 && (
        <Reveal>
          <Card className="p-4 mt-5 border-amber/40" data-testid="ops-alerts">
            <div className="kicker mb-2 text-amber flex items-center gap-2"><Siren className="w-4 h-4" /> New escalation alerts</div>
            <div className="space-y-1.5">
              {alerts.filter((a) => !a.read).slice(0, 5).map((a) => (
                <div key={a.id} className="flex items-center justify-between gap-3 text-sm" data-testid={`ops-alert-${a.id}`}>
                  <span className="truncate"><span className="font-medium">{a.title}</span> <span className="text-slate-400">— {a.body}</span></span>
                  <button onClick={async () => { await api.post(`/ops/notifications/${a.id}/read`); loadAlerts(); }}
                    className="text-[11px] font-mono text-slate-400 hover:text-jade shrink-0">dismiss</button>
                </div>
              ))}
            </div>
          </Card>
        </Reveal>
      )}

      <div className="grid lg:grid-cols-5 gap-5 mt-8">
        <div className="lg:col-span-2 space-y-3">
          <div className="kicker mb-1">{t('queue')} ({claims.length})</div>
          {claims.length === 0 && <Card className="p-6 text-slate-400 text-sm" data-testid="ops-empty">{t('no_escalations')}</Card>}
          {claims.map((c) => (
            <button key={c.id} data-testid={`ops-claim-${c.claim_no}`} onClick={() => { setActive(c); setDone(''); }}
              className={`card w-full text-left p-4 transition-colors ${active?.id === c.id ? 'border-jade/50' : ''}`}>
              <div className="flex items-center justify-between">
                <span className="font-mono text-sm">{c.claim_no}</span>
                <Badge tone={c.status === 'escalated' ? 'amber' : 'slate'}>{c.status}</Badge>
              </div>
              <div className="text-sm mt-2">{c.vehicle.make} {c.vehicle.model}</div>
              {c.assessment && <div className="text-xs text-slate-500 mt-1">{vnd(c.assessment.repair_estimate.total_vnd)} · risk {c.assessment.risk.level}</div>}
            </button>
          ))}
        </div>

        <div className="lg:col-span-3">
          {!active ? (
            <Card className="p-10 text-center text-slate-500"><Gauge className="w-10 h-10 mx-auto mb-3 text-slate-600" />{t('select_claim')}</Card>
          ) : (
            <div className="space-y-4" data-testid="ops-detail">
              <Card className="p-5">
                <div className="flex items-center justify-between">
                  <div><div className="font-mono text-lg">{active.claim_no}</div><div className="text-sm text-slate-400">{active.policy_no} · {active.vehicle.make} {active.vehicle.model}</div></div>
                  <Badge tone={active.status === 'escalated' ? 'amber' : active.status === 'approved' ? 'jade' : 'slate'}>{active.status}</Badge>
                </div>
                {active.incident?.summary && <div className="text-sm text-slate-300 mt-3">{active.incident.summary}</div>}
              </Card>

              {a && (
                <>
                  <CoverageBanner coverage={a.coverage_check} />
                  <Card className="p-5">
                    <div className="kicker mb-2">{t('damage_found')} · {t('repair_est')}</div>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {a.detected_damages.map((d, i) => <Badge key={i} tone={d.severity === 'severe' ? 'crimson' : d.severity === 'moderate' ? 'amber' : 'slate'}>{d.part} · {d.severity}</Badge>)}
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-400">{t('coverage_check')}: <span className={a.coverage_check.covered ? 'text-jade' : 'text-crimson'}>{a.coverage_check.covered ? t('covered') : t('not_covered')}</span></span>
                      <span className="font-mono text-jade">{vnd(a.repair_estimate.total_vnd)}</span>
                    </div>
                    <div className="mt-2 text-sm flex items-center justify-between">
                      <span className="text-slate-400">{t('risk_level')}: <Badge tone={a.risk.level === 'HIGH' ? 'crimson' : a.risk.level === 'MEDIUM' ? 'amber' : 'jade'}>{a.risk.level}</Badge></span>
                      <span className="text-slate-400">{t('ai_conf')}: <span className="font-mono text-jade">{Math.round(a.ai_confidence * 100)}%</span></span>
                    </div>
                  </Card>

                  {active.evidence?.length > 0 && (
                    <Card className="p-5">
                      <div className="kicker mb-3 flex items-center gap-2"><ImageIcon className="w-3.5 h-3.5" /> Evidence ({active.evidence.length})</div>
                      <div className="space-y-1">
                        {active.evidence.map((ev, i) => (
                          <div key={i} className="text-xs text-slate-400 flex justify-between"><span>{ev.label}{ev.is_baseline ? ' (baseline)' : ''}</span><span className="font-mono">conf {Math.round((ev.detection?.confidence || 0) * 100)}%</span></div>
                        ))}
                      </div>
                    </Card>
                  )}
                </>
              )}

              <Card className="p-5">
                <div className="kicker mb-3">Adjuster action</div>
                <Input data-testid="ops-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('adjuster_note')} />
                {done && <div className="text-jade text-sm mt-3 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" />{done}</div>}
                <div className="grid grid-cols-3 gap-2 mt-4">
                  <Button data-testid="ops-approve" onClick={() => act('approve')} disabled={loading}><CheckCircle2 className="w-4 h-4" /> {t('approve')}</Button>
                  <Button variant="ghost" data-testid="ops-request" onClick={() => act('request_more')} disabled={loading}><FileCheck className="w-4 h-4" /></Button>
                  <Button variant="ghost" data-testid="ops-reject" onClick={() => act('reject')} disabled={loading} className="!text-crimson !border-crimson/40"><XCircle className="w-4 h-4" /></Button>
                </div>
                {loading && <div className="mt-3"><Spinner label="…" /></div>}
              </Card>

              {active.settlement_tracking && <SettlementTracker claimId={active.id} />}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
